import { Navigate } from 'react-router-dom';
import { getDefaultRoute } from '@shared/config';
import { useSession, type SessionUserRole } from '@entities/session';

interface RoleGuardProps {
  /** Child elements to render if role is allowed */
  children: React.ReactNode;
  /** Allowed roles */
  allowedRoles: SessionUserRole[];
  /** Optional: Custom fallback element */
  fallback?: React.ReactNode;
  /** Optional: Redirect path if not allowed (default: feature-flag-based) */
  redirectTo?: string;
}

/**
 * Route guard by role — the only one in the app.
 *
 * Until 2026-09-11 this was marked `@deprecated Use CanAccess from
 * @shared/ui`. `CanAccess` never existed; following the hint meant searching
 * for a component that isn't there. There are two areas — the office
 * (`admin`, `employee`) and `/e` for educators — and this check is all it
 * takes to keep them apart.
 *
 * ```tsx
 * <RoleGuard allowedRoles={['admin', 'employee']} redirectTo="/e">
 *   <AppLayout />
 * </RoleGuard>
 * ```
 *
 * It only separates the areas in the UI. The actual boundary is drawn by
 * firestore.rules, storage.rules and `requireRole` in the functions.
 */
export function RoleGuard({
  children,
  allowedRoles,
  fallback,
  redirectTo,
}: RoleGuardProps) {
  const { user } = useSession();
  const defaultRedirect = redirectTo ?? getDefaultRoute(user?.role ?? 'employee');

  // No user or role not allowed
  if (!user || !allowedRoles.includes(user.role)) {
    // Show fallback if provided
    if (fallback) {
      return <>{fallback}</>;
    }

    // Otherwise redirect
    return <Navigate to={defaultRedirect} replace />;
  }

  // Role is allowed - render children
  return <>{children}</>;
}
